import { useState } from 'react';
import { X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function CreateTicketModal({ services, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    service_id: '',
    title: '',
    description: '',
    priority: 'medium'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${API}/tickets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to create ticket');
      }

      onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="relative w-full max-w-2xl mx-4">
        <div className="glass rounded-2xl p-8 glow-box">
          <div className="flex justify-between items-start mb-6">
            <h2 className="text-3xl font-bold text-[#00f0ff] glow">Create Support Ticket</h2>
            <button
              onClick={onClose}
              className="text-[#ff00ff] hover:text-[#ff0080] transition-colors"
              data-testid="close-modal-button"
            >
              <X size={24} />
            </button>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-500/20 border border-red-500 rounded-lg text-red-400" data-testid="modal-error-message">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Diagnostic Fee Notice */}
            <div className="p-4 rounded-lg border-2 border-[#ff00ff] bg-[#ff00ff]/10">
              <p className="text-[#ff00ff] font-bold text-center mb-2">💳 $75 Diagnostic Call Fee Applies to Most Services</p>
              <p className="text-[#00f0ff] text-sm text-center">
                This fee applies toward your total service cost. For example: If you select Virus Removal ($120), you'll pay $75 now, then only $45 upon completion.
              </p>
              <p className="text-[#00ff00] text-xs text-center mt-2 font-bold">
                ✨ Remote Device Troubleshooting is a flat $60 with NO diagnostic fee!
              </p>
              <p className="text-[#00f0ff] text-xs text-center mt-2">
                💬 General Questions and 🌐 Custom Web Services are billed differently — no upfront charge.
              </p>
            </div>

            <div>
              <label className="block text-[#00f0ff] mb-2 font-semibold">Service</label>
              <select
                value={formData.service_id}
                onChange={(e) => setFormData({ ...formData, service_id: e.target.value })}
                className="w-full px-4 py-3 bg-[#0a0a1f]/50 border border-[#00f0ff]/30 rounded-lg text-[#00f0ff] focus:border-[#ff00ff] focus:outline-none"
                required
                data-testid="service-select"
              >
                <option value="">Select a service...</option>
                {services.map(service => {
                  let suffix;
                  if (service.pricing_label) {
                    suffix = ` — ${service.pricing_label}`;
                  } else if (service.name.includes('Remote')) {
                    suffix = ` — $${service.base_price} (FLAT RATE)`;
                  } else {
                    suffix = ` — $${service.base_price} (includes $75 diagnostic fee)`;
                  }
                  return (
                    <option key={service.service_id} value={service.service_id}>
                      {`${service.icon} ${service.name}${suffix}`}
                    </option>
                  );
                })}
              </select>
            </div>

            <div>
              <label className="block text-[#00f0ff] mb-2 font-semibold">Title</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-4 py-3 bg-[#0a0a1f]/50 border border-[#00f0ff]/30 rounded-lg text-[#00f0ff] focus:border-[#ff00ff] focus:outline-none"
                placeholder="Brief description of your issue"
                required
                data-testid="title-input"
              />
            </div>

            <div>
              <label className="block text-[#00f0ff] mb-2 font-semibold">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-4 py-3 bg-[#0a0a1f]/50 border border-[#00f0ff]/30 rounded-lg text-[#00f0ff] focus:border-[#ff00ff] focus:outline-none min-h-[120px]"
                placeholder="Provide details about your issue..."
                required
                data-testid="description-input"
              />
            </div>

            <div>
              <label className="block text-[#00f0ff] mb-2 font-semibold">Priority</label>
              <select
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                className="w-full px-4 py-3 bg-[#0a0a1f]/50 border border-[#00f0ff]/30 rounded-lg text-[#00f0ff] focus:border-[#ff00ff] focus:outline-none"
                data-testid="priority-select"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>

            <div className="flex space-x-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-6 py-3 border-2 border-[#00f0ff] text-[#00f0ff] font-bold rounded-lg hover:bg-[#00f0ff] hover:text-[#0a0a1f] transition-all"
                data-testid="cancel-button"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-6 py-3 btn-primary text-white font-bold rounded-lg disabled:opacity-50"
                data-testid="submit-ticket-button"
              >
                {loading ? 'Creating...' : 'Create Ticket'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default CreateTicketModal;
